import type { ComponentChildren } from "preact";
import { useEffect, useId, useRef, useState } from "preact/hooks";
import { BrandMark, Icon, Logotype, Wordmark } from "./Icon.tsx";
import type { IconName } from "./Icon.tsx";
import { Breadcrumb } from "./Breadcrumb.tsx";
import type { Crumb } from "./Breadcrumb.tsx";

export interface NavigationItem {
  label: string; href: string; icon?: IconName; current?: boolean; count?: string | number;
  /** A dot on the item's icon, which the collapsed rail shows too, for something new there that no count measures; its words for a screen reader. */
  dot?: string;
  /** Why the page can't be opened yet. The item stays in the navigation, dimmed and not a link, with this as its hint. */
  unavailable?: string;
  /** Opens outside the app, in a new tab, and shows the external mark. */
  external?: boolean;
}
export interface NavigationHeading { heading: string }
/** One WarmHub app in the wordmark's switcher: what it's for, and where it lives in this environment. */
export interface AppLink { name: string; href: string; description: string; icon: IconName; current?: boolean }
export type NavigationEntry = NavigationItem | NavigationHeading;
export type Density = "comfortable" | "compact";
// See Design > UI, "Curator Studio assets". Navigation stays real links.
// Between 721 and 1439 pixels it collapses to an icon rail that opens in glass on hover or focus; give every item an icon.
// `account` (an AccountMenu) sits at the sidebar's foot, with `foot` links such as Help just above it.
// On phones a top bar holds the menu button and the wordmark, and the whole sidebar, account included,
// slides in from the left over a scrim; Escape, the scrim, the close button or choosing a page closes it.
// With `apps`, the sidebar's wordmark is a button that opens a menu of the WarmHub apps, the current one checked.
export function AppLayout({ product, navigation, foot = [], home, context, contextIcon, utility, account, apps, density = "comfortable", flush = false, children }: {
  product: string; navigation: ReadonlyArray<NavigationEntry>; foot?: ReadonlyArray<NavigationItem>; /** Links to the app's top level, above the context, such as its home and what spans every context: they keep their icons in the collapsed rail. */ home?: NavigationItem | ReadonlyArray<NavigationItem>; context?: ComponentChildren; contextIcon?: IconName;
  utility?: ComponentChildren; account?: ComponentChildren; density?: Density;
  /** The WarmHub apps someone can switch between, this one marked `current`. With fewer than two, the wordmark stays plain. */
  apps?: ReadonlyArray<AppLink>;
  /** The page draws its own frame edge to edge: a PageBar and a Workspace, with no padding around them. */
  flush?: boolean; children: ComponentChildren;
}) {
  const [open, setOpen] = useState(false);
  const toggle = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLElement>(null);
  const navId = useId();
  const appsId = useId();
  const switcher = apps !== undefined && apps.length > 1;
  useEffect(() => {
    const narrow = matchMedia("(max-width: 720px)");
    const reset = () => { if (!narrow.matches) setOpen(false); };
    narrow.addEventListener("change", reset);
    return () => narrow.removeEventListener("change", reset);
  }, []);
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    const onKey = (event: KeyboardEvent) => { if (event.key === "Escape") close(); };
    addEventListener("keydown", onKey);
    addEventListener("hashchange", close);
    const root = document.documentElement;
    const overflow = root.style.overflow;
    root.style.overflow = "hidden";
    panel.current?.querySelector<HTMLElement>(".ui-app__close")?.focus();
    return () => {
      removeEventListener("keydown", onKey);
      removeEventListener("hashchange", close);
      root.style.overflow = overflow;
      if (panel.current?.contains(document.activeElement)) toggle.current?.focus();
    };
  }, [open]);
  return <div class="ui-app" data-density={density === "compact" ? "compact" : undefined} data-nav-open={open ? "" : undefined}><a class="ui-skip" href="#ui-app-content" onClick={event => { event.preventDefault(); document.getElementById("ui-app-content")?.focus(); }}>Skip to content</a>
    <header class="ui-app__topbar">
      <button ref={toggle} type="button" class="ui-btn ui-btn--ghost ui-btn--icon ui-app__toggle" aria-label="Open navigation" aria-expanded={open} aria-controls={navId} onClick={() => setOpen(true)}><Icon name="menu" /></button>
      <Wordmark product={product} />
    </header>
    <div class="ui-app__scrim" aria-hidden="true" onClick={() => setOpen(false)} />
    <aside ref={panel} id={navId} class="ui-app__nav">
      <button type="button" class="ui-btn ui-btn--ghost ui-btn--icon ui-app__close" aria-label="Close navigation" onClick={() => setOpen(false)}><Icon name="close" /></button>
      {switcher ? <button type="button" class="ui-wordmark ui-app__switch" popovertarget={appsId} aria-label={`${product}: switch app`}>
        <BrandMark /><span><Logotype /><span class="ui-wordmark__product">{product}</span></span><Icon name="selector" size={16} class="ui-app__switch-chevron" />
      </button> : <Wordmark product={product} />}{home && <nav class="ui-app__home" aria-label={`${product}, top level`}>{[home].flat().map(entry => navLink(entry))}</nav>}{context && <div class={`ui-app__context${contextIcon ? " ui-app__context--icon" : ""}`}>{contextIcon && <Icon name={contextIcon} class="ui-app__context-icon" />}<div class="ui-app__context-body">{context}</div></div>}
      <div class="ui-app__menu"><nav aria-label={product}>
        {navigation.map(entry => "heading" in entry ? <span key={`heading-${entry.heading}`} class="ui-app__group">{entry.heading}</span> : navLink(entry))}
      </nav></div>{utility && <div class="ui-app__utility">{utility}</div>}
      {foot.length > 0 && <nav class="ui-app__foot" aria-label={`${product}, more`}>{foot.map(entry => navLink(entry))}</nav>}
      {account && <div class="ui-app__account">{account}</div>}
      {switcher && <AppMenu id={appsId} apps={apps} />}
    </aside><main id="ui-app-content" class={flush ? "ui-app__main ui-app__main--flush" : "ui-app__main"} tabIndex={-1}>{children}</main>
  </div>;
}
/** The wordmark's menu: each app with what it's for. The current one is filled and checked; the others link to it. */
function AppMenu({ id, apps }: { id: string; apps: ReadonlyArray<AppLink> }) {
  return <div id={id} popover="auto" class="ui-app__apps">
    <p class="ui-app__apps-label">Switch app</p>
    {apps.map((app) => {
      const body = <><span class="ui-app__app-icon"><Icon name={app.icon} size={16} /></span>
        <span class="ui-app__app-text"><strong>{app.name}</strong><span>{app.description}</span></span>
        <Icon name={app.current ? "check" : "arrow"} size={16} class="ui-app__app-mark" /></>;
      return app.current ? <a key={app.name} class="ui-app__app" aria-current="page">{body}</a> : <a key={app.name} class="ui-app__app" href={app.href}>{body}</a>;
    })}
  </div>;
}
function navLink(entry: NavigationItem) {
  const body = <>{entry.icon && <Icon name={entry.icon} />}<span class="ui-app__label">{entry.label}</span>{entry.count !== undefined && <span class="ui-app__count">{entry.count}</span>}
    {entry.dot !== undefined && <><span class="ui-app__dot" aria-hidden="true" /><span class="ui-sr-only"> ({entry.dot})</span></>}</>;
  // An anchor without href is not a link and takes no focus, so it reads as plain text that keeps the row's look.
  if (entry.unavailable) return <a key={entry.href} aria-disabled="true" title={entry.unavailable}>{body}<span class="ui-sr-only"> ({entry.unavailable})</span></a>;
  if (entry.external) return <a key={entry.href} href={entry.href} target="_blank" rel="noreferrer">{body}<Icon name="external" size={16} class="ui-app__external" /><span class="ui-sr-only"> (opens in a new tab)</span></a>;
  return <a key={entry.href} href={entry.href} aria-current={entry.current ? "page" : undefined}>{body}</a>;
}
export function PageHeader({ title, description, crumbs, action }: {
  title: ComponentChildren; description?: string; crumbs?: ReadonlyArray<Crumb>; action?: ComponentChildren;
}) {
  return <header class="ui-page-header">{crumbs && <Breadcrumb crumbs={crumbs} />}
    <div class="ui-page-header__row"><div><h1>{title}</h1>{description && <p>{description}</p>}</div>{action}</div>
  </header>;
}
export function DetailLayout({ children, rail }: { children: ComponentChildren; rail: ComponentChildren }) {
  return <div class="ui-detail"><div class="ui-detail__main">{children}</div><aside class="ui-detail__rail">{rail}</aside></div>;
}
